import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { Link } from 'react-router';
import { formatCurrencyNoDecimals, formatDate } from '@/utils/formatters';
import {
  useRecentActivity,
  type RecentActivityItem,
} from '../hooks/useRecentActivity';

function ActivityList({
  isLoading,
  activities,
}: {
  isLoading: boolean;
  activities: RecentActivityItem[];
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (activities.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-muted-foreground">
          No recent activity to display
        </p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-border">
      {activities.map((activity) => {
        const isDistribution = activity.type === 'distribution';
        const detailPath = isDistribution
          ? `/portal/transactions/distributions/${activity.id}`
          : `/portal/transactions/drawdowns/${activity.id}`;

        return (
          <Link
            key={activity.id}
            to={detailPath}
            className="flex w-full items-center gap-3 py-3 px-6 text-left transition-colors hover:bg-muted/50"
          >
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 ${
                isDistribution ? 'bg-emerald-500/10' : 'bg-muted'
              }`}
            >
              {isDistribution ? (
                <ArrowDownLeft className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {activity.fund_name}
              </p>
              <p className="text-xs text-muted-foreground">
                {activity.description} · {formatDate(activity.date)}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p
                className={`font-semibold text-sm ${
                  isDistribution
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-muted-foreground'
                }`}
              >
                {isDistribution ? '+' : '\u2212'}
                {formatCurrencyNoDecimals(activity.amount, activity.currency)}
              </p>
              <p
                className={`text-xs ${
                  isDistribution
                    ? 'text-emerald-600/70 dark:text-emerald-400/70'
                    : 'text-muted-foreground/70'
                }`}
              >
                {activity.status}
              </p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function RecentActivity() {
  const { data: activities = [], isLoading } = useRecentActivity();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Latest transactions and updates</CardDescription>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <ActivityList isLoading={isLoading} activities={activities} />
      </CardContent>
    </Card>
  );
}
